"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { Calendar, MapPin, DollarSign, CheckCircle } from "lucide-react";
import { useTranslations } from "next-intl";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

export default function MissionShowcaseSection() {
  const t = useTranslations("MissionShowcase");

  return (
    <div className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900">{t("title")}</h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">{t("subtitle")}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-5xl"
        >
          <Card className="overflow-hidden">
            <div className="grid gap-0 lg:grid-cols-2">
              <div className="bg-gray-50 p-8">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">{t("sampleTitle")}</h3>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{t("badgeInProgress")}</Badge>
                </div>

                <div className="mb-8 space-y-4">
                  <div className="flex items-start">
                    <MapPin className="mt-1 mr-3 h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">{t("locationLabel")}</div>
                      <div className="text-sm text-gray-600">{t("locationValue")}</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Calendar className="mt-1 mr-3 h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">{t("timelineLabel")}</div>
                      <div className="text-sm text-gray-600">{t("timelineValue")}</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <DollarSign className="mt-1 mr-3 h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">{t("budgetLabel")}</div>
                      <div className="text-sm text-gray-600">{t("budgetValue")}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="mb-4 font-semibold text-gray-900">{t("milestonesTitle")}</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <CheckCircle className="mr-3 h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{t("milestone1")}</span>
                      </div>
                      <span className="text-xs text-green-600">{t("milestone1Status")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full border-2 border-accent">
                        <div className="h-2 w-2 rounded-full bg-accent" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{t("milestone2")}</span>
                      </div>
                      <span className="text-xs text-accent">{t("milestone2Status")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="mr-3 h-5 w-5 rounded-full border-2 border-gray-300" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-500">{t("milestone3")}</span>
                      </div>
                      <span className="text-xs text-gray-500">{t("milestone3Status")}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Button variant="outline" className="flex-1" type="button">
                    {t("btnViewDetails")}
                  </Button>
                  <Button className="flex-1 bg-accent text-white hover:bg-accent/90" type="button">
                    {t("btnMessagePilot")}
                  </Button>
                </div>
              </div>

              <div className="relative flex min-h-[500px] items-start justify-center bg-gray-200">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20">
                  <Image
                    src={t("missionImageSrc")}
                    alt={t("mapAlt")}
                    fill
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="object-cover object-[50%_80%]"
                  />
                </div>
                <div className="relative z-10 mx-4 mt-8 max-w-md rounded-lg bg-white/80 p-8 text-center backdrop-blur-sm">
                  <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg">
                    <MapPin className="h-10 w-10 text-accent" />
                  </div>
                  <h4 className="mb-2 text-xl font-semibold text-gray-900">{t("mapOverlayTitle")}</h4>
                  <p className="text-gray-600">{t("mapOverlayBody")}</p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <div className="mt-12 text-center">
          <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" asChild>
            <Link href={t("ctaExploreHref")}>{t("ctaExplore")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
